import type { AlineEditor, DocParts } from '@aline/common';
import {
  alineToHtmlForClipboard,
  AlineTransforms,
  composePlugins,
  createWithCollaboration,
  createWithCursors,
  createWithLocalHistory,
  HistoryTransforms,
  isAlineContentEditor,
  Queries,
  SelEventSource,
  withApply,
  withCatchYjsErrors,
  withCorePlugins,
  withHeaderFooterCacheInvalidation,
  withValidation,
} from '@aline/common';
import type { ApolloClient } from '@apollo/client';
import type { CursorEditor } from '@slate-yjs/core';
import { Encoder as CborEncoder } from 'cbor-x';
import { clone } from 'ramda';
import type { Location } from 'slate';
import { createEditor, Editor } from 'slate';
import { serializeSelectionFromRange } from 'src/components/ai/chat/chat-utils/serializeSelectionFromRange';
import type {
  AlineClipboardData,
  AlineSelectionClipboardDataV3,
} from 'src/editor/transforms/clipboard-schemas';
import type { AlineClientEditor } from 'src/layout-engine';
import {
  ClipboardMime,
  encodeSlateFragment,
  ReactEditor,
  withHistory,
  withReact,
} from 'src/layout-engine';
import { pushNotification } from 'src/notifications';
import { Sentry } from 'src/sentry';
import type { Awareness } from 'y-protocols/awareness';
import type { XmlText } from 'yjs';

type CreateClientEditorOptions<TCollaborative extends boolean> = TCollaborative extends true
  ? {
      awareness: Awareness;
      client: ApolloClient;
      collaborative: true;
      documentId: UUID;
      sharedRoot: XmlText;
    }
  : {
      client: ApolloClient;
      documentId: UUID;
      collaborative?: false;
    };

export const CLIPBOARD_ALINE_DATA_ATTR = 'data-aline-clipboard';
export const dataAlineFragmentWrapper = 'data-aline-fragment-wrapper';

/**
 * Creates Aline selection clipboard data for pasting into Chat Editor.
 * Includes document info and expanded context with surrounding paragraphs.
 *
 * @param editor - Editor instance
 * @returns JSON string of clipboard data, or null if no selection or documentId
 */
function createAlineSelectionClipboard(editor: Editor): string | null {
  if (!isAlineContentEditor(editor) || !editor.selection || !editor.documentInfo) return null;

  try {
    const html = serializeSelectionFromRange(editor, editor.selection);

    return JSON.stringify({
      documentId: editor.documentInfo.id,
      documentName: editor.documentInfo.name || 'Untitled Document',
      focusSuggestionId: null,
      html,
    } satisfies AlineSelectionClipboardDataV3);
  } catch (err) {
    console.warn('Failed to create selection clipboard data:', err);
    return null;
  }
}

export const generalTransforms = {
  /**
   * Copy content to the clipboard.
   * - Supports modern browsers with the custom Slate MIME type.
   * - Fallback for browsers (like Firefox) that don't support custom MIME types:
   *   - Encodes the Slate fragment into a `data-slate-fragment` attribute in HTML
   * @returns true if the content was copied to the clipboard, false otherwise.
   *
   * TODO: If changing this function, consider adding calls to AlineTransforms.announce to improve
   * screen reader support as per EDI-395.
   */
  copyToClipboard: async (
    editor: Editor,
    options: { at?: Location; documentId?: UUID } = {}
  ): Promise<boolean> => {
    const { at = editor.selection, documentId } = options;
    if (!at) return false;

    /**
     * Copying a grid (table cell rectangle) selection produces a standalone table fragment of
     * exactly the selected cells.
     */
    const gridFragmentTable =
      options.at === undefined ? Queries.getGridSelectionFragment(editor) : null;

    const fragment = gridFragmentTable
      ? [gridFragmentTable]
      : Queries.fragmentAtLocation(editor, { at });
    if (!fragment.length) return false;

    const htmlContent = alineToHtmlForClipboard(
      fragment,
      Queries.getNumbering(editor).listDefinitions
    );
    const slateContent = JSON.stringify(fragment);
    const string = Queries.nodeString(fragment, { paragraphSeparator: '\n\n' });

    try {
      /**
       * iOS Firefox (and some older Safari) lack `ClipboardItem` and
       * `navigator.clipboard.write`. Fall back to plain-text writeText so
       * copying still works — the rich slate/HTML payload is only useful
       * when pasting back into Aline, which mobile users don't do.
       */
      if (typeof ClipboardItem === 'undefined' || !navigator.clipboard?.write) {
        await navigator.clipboard.writeText(string);
        return true;
      }

      const encodedSlateFragment = encodeSlateFragment(slateContent);
      const alineSelectionData = createAlineSelectionClipboard(editor);
      const clipboardData: AlineClipboardData = {
        documentId,
        selection: alineSelectionData || undefined,
        slate: encodedSlateFragment,
      };
      const encodedClipboardData = JSON.stringify(clipboardData);

      let htmlWithFragment: string;

      const tempContainerElement = document.createElement('div');
      tempContainerElement.innerHTML = htmlContent;

      // Attach consolidated clipboard data to the first real HTML element if it exists.
      // This preserves the original HTML structure as much as possible without adding extra wrappers.
      // Only when there are no elements, the fallback `<div>` wrapping is used.
      const firstChild = tempContainerElement.firstElementChild;
      if (firstChild) {
        // Store consolidated data in single attribute
        firstChild.setAttribute(CLIPBOARD_ALINE_DATA_ATTR, encodedClipboardData);
        htmlWithFragment = tempContainerElement.innerHTML;
      } else {
        tempContainerElement.setAttribute(CLIPBOARD_ALINE_DATA_ATTR, encodedClipboardData);
        tempContainerElement.setAttribute(dataAlineFragmentWrapper, 'true');
        htmlWithFragment = tempContainerElement.outerHTML;
      }

      const isSlateMimeSupported = ClipboardItem.supports?.(ClipboardMime.slate);
      const isAlineSelectionMimeSupported = ClipboardItem.supports?.(ClipboardMime.alineSelection);

      /**
       * Aline clipboard data is embedded in HTML as a data attribute for cross-browser compatibility.
       */
      const clipboardItem = new ClipboardItem({
        [ClipboardMime.text]: new Blob([string], { type: ClipboardMime.text }),
        [ClipboardMime.html]: new Blob([htmlWithFragment], { type: ClipboardMime.html }),
        ...(isSlateMimeSupported
          ? { [ClipboardMime.slate]: new Blob([slateContent], { type: ClipboardMime.slate }) }
          : {}),
        ...(alineSelectionData && isAlineSelectionMimeSupported
          ? {
              [ClipboardMime.alineSelection]: new Blob([alineSelectionData], {
                type: ClipboardMime.alineSelection,
              }),
            }
          : {}),
      });
      await navigator.clipboard.write([clipboardItem]);
      return true;
    } catch (error) {
      const isNotAllowedError = error instanceof Error && error.name === 'NotAllowedError';
      const errorMessage = isNotAllowedError
        ? 'Unable to access clipboard. Please ensure you have granted clipboard permissions.'
        : 'Failed to copy to clipboard. Contact support if the issue persists.';
      pushNotification({
        message: errorMessage,
        variant: 'error',
      });
      console.error('Error copying to clipboard:', error);
      return false;
    }
  },

  /**
   * Create an Editor object for the Client.
   *
   * NOTE: The order of plugins matters.
   * (1) Since withApply, withReadOnly, and withHistory each override editor.apply, it's necessary for the order to be withApply -> withHistory -> withReadOnly.
   * (2) Since withPagination and withValidation each override editor.normalizeNode, it's necessary for the order to be withPagination -> withValidation.
   * (3) withValidation should be last so it's editor.normalizeNode is called first and the node(s) is validated before other funcs are called.
   * In short, pay attention to plugin order if you modify this initialization function.
   */
  createClientEditor: <TCollaborative extends boolean>(
    args: CreateClientEditorOptions<TCollaborative>
  ): TCollaborative extends true
    ? AlineClientEditor & CursorEditor & TCollaborative
    : AlineClientEditor & CursorEditor => {
    const { client } = args;
    const collaborationPlugins = args.collaborative
      ? composePlugins(
          createWithCollaboration({ sharedRoot: args.sharedRoot }),
          createWithCursors({ awareness: args.awareness })
        )
      : <T>(e: T) => e;
    const withPlugins = composePlugins(
      withApply,
      e => withReact(e, { client }),
      collaborationPlugins,
      args.collaborative ? createWithLocalHistory() : withHistory,
      withCorePlugins,
      withHeaderFooterCacheInvalidation,
      withValidation,
      e =>
        withCatchYjsErrors(e, {
          onError: ({ context, error, isLocal }) => {
            console.error(error);

            /**
             * Encode the error context using CBOR to reduce the chance of us exceeding Sentry's
             * size limits. The pack option deduplicates strings, which provides significant
             * compression for Slate values due to its repetitive property names.
             */
            const scrubbedContext = Queries.scrub(context);
            const encoder = new CborEncoder({ pack: true });
            const encodedContext = encoder.encode(scrubbedContext);

            Sentry.captureException(error, {
              attachments: [{ data: encodedContext, filename: 'slate-yjs-context.cbor' }],
            });

            pushNotification({
              message: `Error ${isLocal ? 'when editing the document' : 'while applying changes by other users'}. Contact support if the issue persists.`,
              variant: 'error',
            });
          },
        })
    );
    const editor = withPlugins(createEditor());
    const alineEditorType: AlineEditor['type'] = 'AlineEditor';
    editor.type = alineEditorType;

    return editor;
  },

  /**
   * Create a clone of the editor.  This is useful in situations where you want to process editor.children but not change the content of the actual document.
   * For instance, when building a contract instance JSON, exporting a Word doc, etc.
   */
  createEditorClone: (editor: Editor): Editor => {
    return clone(editor);
  },

  /**
   * Safely focus the editor.  It uses Promise.resolve because certain components (e.g., like a native <select />) will
   * "steal focus" from the editor if we just use ReactEditor.focus directly.
   */
  focus: (editor: Editor): void => {
    Promise.resolve().then(() => {
      ReactEditor.focus(editor);
    });
  },

  /**
   * Initialize the Editor.  Useful for loading a Document and also loading different Document
   * versions programmatically.
   */
  initializeEditor: (
    editor: AlineClientEditor,
    options: {
      newState: NonNullable<AlineEditor['userState']>;
      newChildren?: DocParts;
      select?: boolean;
    }
  ): void => {
    const { newChildren, newState, select = true } = options;

    editor.userState = newState;
    editor.renderModel = undefined;
    editor.selection = null;
    editor.marks = null;
    HistoryTransforms.clearHistory(editor);

    // Normalize the entire doc to normalize the content model and rebuild the render model.
    if (newChildren) editor.children = newChildren;

    // Normalize the document to build the cache of render model nodes.
    Editor.normalize(editor, { force: true });
    // Ensure we *always* call onChange, even if the document was already normalized
    editor.onChange();

    if (select) {
      const [, documentPath] = Queries.getDocument(editor);
      const documentStart = Editor.start(editor, documentPath);

      AlineTransforms.setSelection(editor, {
        eventSource: SelEventSource.PROGRAMMATIC,
        props: { affinity: 'forward', anchor: documentStart, focus: documentStart },
      });
    }
  },
};
